/**
 * 학생 통계 페이지
 * 학습 통계, 목표, 상담 기록을 한눈에 볼 수 있는 페이지
 */
import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  ClockIcon,
  FireIcon,
  AcademicCapIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  TrophyIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import { studentStatisticsService } from '../services/StudentStatisticsService';
import type {
  StudyTimeData,
  WeeklyStats,
  StudyGoal,
  CounselingRecordSummary,
} from '../models/StudentStatistics';

const StudentStatisticsPage: React.FC = () => {
  // 상태 관리
  const [selectedPeriod, setSelectedPeriod] = useState<'today' | 'week' | 'month'>('week');
  const [todayStats, setTodayStats] = useState<StudyTimeData | null>(null);
  const [weeklyStats, setWeeklyStats] = useState<WeeklyStats | null>(null);
  const [activeGoals, setActiveGoals] = useState<StudyGoal[]>([]);
  const [counselingRecords, setCounselingRecords] = useState<CounselingRecordSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 사용자 정보 가져오기
  const getUserId = (): string | null => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.id || user.user_id || null;
      } catch {
        return null;
      }
    }
    return null;
  };

  // 데이터 로드
  useEffect(() => {
    loadStatistics();
  }, [selectedPeriod]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError(null);

      // 먼저 today와 weekly 통계를 로드 (토큰 기반이므로 userId 불필요)
      const [today, weekly] = await Promise.allSettled([
        studentStatisticsService.getTodayStudyTime(),
        studentStatisticsService.getWeeklyStats(),
      ]);

      if (today.status === 'fulfilled') setTodayStats(today.value);
      if (weekly.status === 'fulfilled') setWeeklyStats(weekly.value);

      // userId가 필요한 API는 선택적으로 로드
      const userId = getUserId();
      if (userId) {
        const [goals, counseling] = await Promise.allSettled([
          studentStatisticsService.getMyActiveGoals(userId),
          studentStatisticsService.getRecentCounselingRecords(userId, 3),
        ]);

        if (goals.status === 'fulfilled') setActiveGoals(goals.value);
        if (counseling.status === 'fulfilled') setCounselingRecords(counseling.value);
      } else {
        console.warn('userId를 찾을 수 없어 목표 및 상담 기록을 불러올 수 없습니다.');
      }

    } catch (err) {
      console.error('통계 로드 실패:', err);
      setError('통계를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 시간 포맷 함수
  const formatMinutes = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}시간 ${mins}분`;
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">통계를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error}</p>
          <button
            onClick={loadStatistics}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 헤더 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <ChartBarIcon className="h-8 w-8 text-blue-600" />
            나의 학습 리포트
          </h1>
          <p className="mt-2 text-gray-600">학습 통계와 목표 달성 현황을 확인하세요</p>
        </div>

        {/* 기간 선택 탭 */}
        <div className="mb-6 bg-white rounded-lg shadow p-2 inline-flex gap-2">
          <button
            onClick={() => setSelectedPeriod('today')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              selectedPeriod === 'today'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            오늘
          </button>
          <button
            onClick={() => setSelectedPeriod('week')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              selectedPeriod === 'week'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            이번 주
          </button>
          <button
            onClick={() => setSelectedPeriod('month')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              selectedPeriod === 'month'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            이번 달
          </button>
        </div>

        {/* 학습 목표 섹션 */}
        {activeGoals.length > 0 && (
          <div className="mb-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <TrophyIcon className="h-6 w-6" />
                나의 학습 목표
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {activeGoals.map((goal) => (
                <div key={goal.id} className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">
                      {goal.goal_period === 'weekly' ? '주간 목표' : '월간 목표'}
                    </span>
                    <span className="text-xs bg-white/20 px-2 py-1 rounded">
                      D-{goal.remaining_days}
                    </span>
                  </div>
                  {goal.daily_study_hours && (
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span>일일 {goal.daily_study_hours}시간</span>
                        <span className="font-bold">{goal.achievement_rate?.toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-white/20 rounded-full h-2">
                        <div
                          className="bg-white rounded-full h-2 transition-all duration-500"
                          style={{ width: `${Math.min(goal.achievement_rate || 0, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  {goal.notes && (
                    <p className="text-sm text-white/80 mt-2">{goal.notes}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 주요 지표 카드 */}
        {todayStats && weeklyStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* 총 공부 시간 */}
            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ClockIcon className="h-6 w-6 text-blue-600" />
                </div>
                <ArrowTrendingUpIcon
                  className={`h-5 w-5 ${
                    weeklyStats.study_trend === 'increasing'
                      ? 'text-green-500'
                      : weeklyStats.study_trend === 'decreasing'
                      ? 'text-red-500'
                      : 'text-gray-400'
                  }`}
                />
              </div>
              <h3 className="text-gray-600 text-sm font-medium">총 공부 시간</h3>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatMinutes(
                  selectedPeriod === 'today'
                    ? todayStats.pure_study_minutes
                    : weeklyStats.total_study_minutes
                )}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {selectedPeriod === 'week' && `평균 ${formatMinutes(weeklyStats.average_daily_study)}/일`}
              </p>
            </div>

            {/* 평균 집중도 */}
            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FireIcon className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <h3 className="text-gray-600 text-sm font-medium">평균 집중도</h3>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {todayStats.efficiency_rate.toFixed(1)}%
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {todayStats.efficiency_rate >= 80
                  ? '매우 좋음 🔥'
                  : todayStats.efficiency_rate >= 60
                  ? '좋음 👍'
                  : '개선 필요 💪'}
              </p>
            </div>

            {/* 학습 세션 수 */}
            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <AcademicCapIcon className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <h3 className="text-gray-600 text-sm font-medium">학습 세션</h3>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {selectedPeriod === 'today'
                  ? todayStats.study_sessions.filter((s) => s.is_valid).length
                  : weeklyStats.daily_stats.reduce((sum, day) => sum + day.session_count, 0)}
                회
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {selectedPeriod === 'week' && `평균 ${(weeklyStats.daily_stats.reduce((sum, day) => sum + day.session_count, 0) / 7).toFixed(1)}회/일`}
              </p>
            </div>

            {/* 평균 휴식 시간 */}
            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <CalendarIcon className="h-6 w-6 text-orange-600" />
                </div>
              </div>
              <h3 className="text-gray-600 text-sm font-medium">평균 휴식</h3>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatMinutes(todayStats.break_minutes)}
              </p>
              <p className="text-xs text-gray-500 mt-1">적절한 휴식이 중요해요</p>
            </div>
          </div>
        )}

        {/* 일별 공부 시간 그래프 */}
        {weeklyStats && selectedPeriod === 'week' && (
          <div className="bg-white rounded-xl shadow p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <ChartBarIcon className="h-6 w-6 text-blue-600" />
              일별 공부 시간
            </h2>
            <div className="flex items-end justify-between h-64 gap-2">
              {weeklyStats.daily_stats.map((day) => {
                const maxMinutes = Math.max(...weeklyStats.daily_stats.map((d) => d.study_minutes));
                const heightPercent = maxMinutes > 0 ? (day.study_minutes / maxMinutes) * 100 : 0;
                const isBestDay =
                  weeklyStats.best_day && day.date === weeklyStats.best_day.date;

                return (
                  <div key={day.date} className="flex-1 flex flex-col items-center">
                    <div className="w-full bg-gray-100 rounded-t-lg relative" style={{ height: '200px' }}>
                      <div
                        className={`absolute bottom-0 w-full rounded-t-lg transition-all duration-500 ${
                          isBestDay
                            ? 'bg-gradient-to-t from-yellow-400 to-yellow-500'
                            : 'bg-gradient-to-t from-blue-400 to-blue-500'
                        }`}
                        style={{ height: `${heightPercent}%` }}
                      >
                        {day.study_minutes > 0 && (
                          <span className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs font-medium text-gray-700">
                            {formatMinutes(day.study_minutes)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 text-center">
                      <p className="text-xs text-gray-600 font-medium">{formatDate(day.date)}</p>
                      <p className="text-xs text-gray-500">{day.efficiency_rate.toFixed(0)}%</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 최근 상담 기록 */}
        {counselingRecords.length > 0 && (
          <div className="bg-white rounded-xl shadow p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-600" />
                최근 상담 기록
              </h2>
              <button
                onClick={() => window.location.href = '/dashboard/counseling'}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                전체 보기 →
              </button>
            </div>
            <div className="space-y-4">
              {counselingRecords.map((record) => (
                <div
                  key={record.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      {record.counselor_name} 선생님
                    </span>
                    <span className="text-xs text-gray-500">{formatDate(record.counseling_date)}</span>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2">{record.summary}</p>
                  {record.next_steps && (
                    <p className="text-xs text-blue-600 mt-2">다음 목표: {record.next_steps}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 상세 학습 기록 */}
        {todayStats && todayStats.study_sessions.length > 0 && (
          <div className="bg-white rounded-xl shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">상세 학습 기록</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      입실 시간
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      퇴실 시간
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      좌석
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      순공부시간
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      휴식시간
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      상태
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {todayStats.study_sessions
                    .filter((session) => session.is_valid)
                    .map((session, index) => (
                      <tr key={session.session_id || index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(session.check_in).toLocaleTimeString('ko-KR', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {session.check_out
                            ? new Date(session.check_out).toLocaleTimeString('ko-KR', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '진행중'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {session.seat_id}번
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatMinutes(session.pure_study_minutes)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatMinutes(session.break_minutes)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              session.status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {session.status === 'completed' ? '완료' : '진행중'}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentStatisticsPage;
